import axios from 'axios';

// DUAL BACKEND CONFIGURATION
const EXISTING_API_URL = 'https://accountss-loams.onrender.com'; // Existing invoice operations
const NEW_API_URL = 'https://accountss-3.onrender.com'; // New SOA functionality

// Create axios instance for existing Flask API
const existingFlaskApi = axios.create({
    baseURL: EXISTING_API_URL,
    timeout: 30000,
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
    }
});

// Create axios instance for new backend API
const newBackendApi = axios.create({
    baseURL: NEW_API_URL,
    timeout: 30000,
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
    }
});

// Add request interceptors to include auth token for both APIs
existingFlaskApi.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = token;
    }
    return config;
});

newBackendApi.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = token;
    }
    return config;
});

// Helper function to generate formatted filename 
function generateFormattedFilename(serialNumber, taxInvoiceRef, clientName, projectName, paymentCertNo, monthYear, projectCode) {
    const cleanStr = (str) => str ? str.replace(/[\/\\:*?"<>|]/g, '-').trim() : '';
    
    const cleanSerialNumber = cleanStr(serialNumber);
    const cleanTaxInvoiceRef = cleanStr(taxInvoiceRef);
    const cleanClientName = cleanStr(clientName).split(',')[0];
    const cleanProjectName = cleanStr(projectName).split('@')[0].trim();
    const cleanPaymentCertNo = cleanStr(paymentCertNo);
    const cleanProjectCode = cleanStr(projectCode);
    
    return `Tax Invoice # ${cleanSerialNumber}, Inv # ${cleanTaxInvoiceRef}, ${cleanClientName}, ${cleanProjectName}, PC# ${cleanPaymentCertNo}, ${monthYear}, ${cleanProjectCode}.pdf`;
}

const invoiceService = {
    // EXISTING FUNCTIONALITY - Uses original backend
    saveInvoice: async (pdfFile, metadata) => {
        try {
            if (!pdfFile) {
                throw new Error('PDF file is required');
            }

            if (!metadata?.serial_number) {
                throw new Error('Serial number is required');
            }

            console.log('Saving invoice to EXISTING backend:', EXISTING_API_URL);
            const formData = new FormData();
            formData.append('file', pdfFile);

            // Process metadata (keeping existing logic)
            const processedMetadata = {
                serial_number: metadata.serial_number,
                tax_invoice_ref: metadata.tax_invoice_ref,
                tax_invoice_date: metadata.tax_invoice_date,
                client_name: metadata.client_name,
                project_code: metadata.project_code,
                project_name: metadata.project_name,
                loa_wo_sub_contract_ref: metadata.loa_wo_sub_contract_ref,
                amount: parseFloat(metadata.amount || 0),
                trn: metadata.trn || '',
                address: metadata.address || '',
                payment_certificate_no: metadata.payment_certificate_no || '',
                certificate_date: metadata.certificate_date,
                cumulative_value: parseFloat(metadata.cumulative_value || 0),
                previous_payment: parseFloat(metadata.previous_payment || 0),
                custom_description: metadata.custom_description || '',
                description_color: metadata.description_color || 'black',
                custom_amount: parseFloat(metadata.custom_amount || 0),
                advance_release_checked: Boolean(metadata.advance_release_checked),
                advance_release_percentage: parseInt(metadata.advance_release_percentage || 0),
                advance_release_taxable: parseFloat(metadata.advance_release_taxable || 0),
                advance_release_vat: parseFloat(metadata.advance_release_vat || 0),
                advance_release_total: parseFloat(metadata.advance_release_total || 0),
                retention_release_checked: Boolean(metadata.retention_release_checked),
                retention_release_percentage: parseInt(metadata.retention_release_percentage || 0),
                retention_release_taxable: parseFloat(metadata.retention_release_taxable || 0),
                retention_release_vat: parseFloat(metadata.retention_release_vat || 0),
                retention_release_total: parseFloat(metadata.retention_release_total || 0),
                cumulative_retention_checked: Boolean(metadata.cumulative_retention_checked),
                cumulative_retention_percentage: parseInt(metadata.cumulative_retention_percentage || 0),
                cumulative_retention_taxable: parseFloat(metadata.cumulative_retention_taxable || 0),
                cumulative_retention_vat: parseFloat(metadata.cumulative_retention_vat || 0),
                cumulative_retention_total: parseFloat(metadata.cumulative_retention_total || 0),
                cumulative_performance_retention_checked: Boolean(metadata.cumulative_performance_retention_checked),
                cumulative_performance_retention_percentage: parseInt(metadata.cumulative_performance_retention_percentage || 0),
                cumulative_performance_retention_taxable: parseFloat(metadata.cumulative_performance_retention_taxable || 0),
                cumulative_performance_retention_vat: parseFloat(metadata.cumulative_performance_retention_vat || 0),
                cumulative_performance_retention_total: parseFloat(metadata.cumulative_performance_retention_total || 0),
                ncr_deduction_checked: Boolean(metadata.ncr_deduction_checked),
                ncr_deduction_amount: parseFloat(metadata.ncr_deduction_amount || 0),
                ncr_deduction_taxable: parseFloat(metadata.ncr_deduction_taxable || 0),
                ncr_deduction_vat: parseFloat(metadata.ncr_deduction_vat || 0),
                ncr_deduction_total: parseFloat(metadata.ncr_deduction_total || 0),
                previous_payment_taxable: parseFloat(metadata.previous_payment_taxable || 0),
                previous_payment_vat: parseFloat(metadata.previous_payment_vat || 0),
                previous_payment_total: parseFloat(metadata.previous_payment_total || 0),
                other_deduction_checked: Boolean(metadata.other_deduction_checked),
                other_deduction_amount: parseFloat(metadata.other_deduction_amount || 0),
                other_deduction_taxable: parseFloat(metadata.other_deduction_taxable || 0),
                other_deduction_vat: parseFloat(metadata.other_deduction_vat || 0),
                other_deduction_total: parseFloat(metadata.other_deduction_total || 0),
                other_deduction_include_vat: Boolean(metadata.other_deduction_include_vat),
                other_deduction_show_subtotal: Boolean(metadata.other_deduction_show_subtotal),
                bank_name_branch: metadata.bank_name_branch,
                account_number: metadata.account_number,
                iban: metadata.iban,
                swift: metadata.swift,
                landscape_preliminaries: parseFloat(metadata.landscape_preliminaries || 0),
                landscape_works: parseFloat(metadata.landscape_works || 0),
                tenderers_adjustments: parseFloat(metadata.tenderers_adjustments || 0),
                provisional_sums: parseFloat(metadata.provisional_sums || 0),
                swimming_preliminaries: parseFloat(metadata.swimming_preliminaries || 0),
                swimming_mep: parseFloat(metadata.swimming_mep || 0),
                swimming_civil: parseFloat(metadata.swimming_civil || 0),
                total_wd_last_period: parseFloat(metadata.total_wd_last_period || 0)
            };

            // Validate numeric values
            Object.entries(processedMetadata).forEach(([key, value]) => {
                if (typeof value === 'number' && isNaN(value)) {
                    console.warn(`Invalid number found for ${key}, setting to 0`);
                    processedMetadata[key] = 0;
                }
            });

            formData.append('metadata', JSON.stringify(processedMetadata));

            const result = await axios.post(
                `${EXISTING_API_URL}/api/invoices/save`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                    withCredentials: true
                }
            );

            return result.data;
        } catch (error) {
            console.error('Error in saveInvoice:', error);
            if (error.response) {
                throw new Error(error.response.data?.message || error.response.data?.error || 'Server error occurred');
            } else if (error.request) {
                throw new Error('No response from server. Please check your connection.');
            } else {
                throw error;
            }
        }
    },

    // EXISTING FUNCTIONALITY - Get client invoices from original backend
    getClientInvoices: async (clientName) => {
        try {
            console.log('Fetching client invoices from EXISTING backend:', EXISTING_API_URL);
            const response = await existingFlaskApi.get(`/api/invoices/client/${encodeURIComponent(clientName)}`);
            
            if (response.data.status === 'success') {
                return (response.data.invoices || []).map(invoice => ({
                    id: invoice.id,
                    serialNumber: invoice.serial_number,
                    invoiceRef: invoice.invoice_ref,
                    projectCode: invoice.project_code,
                    projectName: invoice.project_name,
                    amount: invoice.amount,
                    createdAt: invoice.created_at,
                    url: invoice.url,
                    client_name: invoice.client_name,
                    status: invoice.status,
                    file_path: invoice.file_path
                }));
            }
            
            return [];
            
        } catch (error) {
            console.error('Error fetching client invoices:', error);
            throw new Error('Failed to fetch invoices');
        }
    },

    // NEW FUNCTIONALITY - SOA operations using new backend
    getClientSOA: async () => {
        try {
            console.log('Fetching SOA data from NEW backend:', NEW_API_URL);
            const response = await newBackendApi.get('/api/soa');

            if (response.data.status === 'success') {
                return response.data.data;
            } else {
                throw new Error(response.data.message || 'Failed to fetch SOA data');
            }
        } catch (error) {
            console.error('Error fetching SOA data:', error);
            throw error;
        }
    },

    downloadClientSOA: async () => {
        try {
            console.log('Downloading SOA from NEW backend:', NEW_API_URL);
            const response = await axios.get(`${NEW_API_URL}/api/soa/download`, {
                headers: {
                    'Authorization': localStorage.getItem('token')
                },
                responseType: 'blob'
            });

            // Create download link
            const blob = new Blob([response.data], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Statement_of_Account_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            return true;
        } catch (error) {
            console.error('Error downloading SOA:', error);
            throw error;
        }
    },

    // EXISTING FUNCTIONALITY - Save invoice with tax invoices list
    saveInvoiceWithTaxInvoicesList: async (pdfFile, metadata) => {
        try {
            if (!pdfFile) {
                throw new Error('PDF file is required');
            }

            if (!metadata?.serial_number) {
                throw new Error('Serial number is required');
            }

            console.log('Saving invoice with taxinvoiceslist to EXISTING backend:', EXISTING_API_URL);
            const formData = new FormData();
            formData.append('file', pdfFile);

            // Format certificate date for filename
            const certDate = new Date(metadata.certificate_date || metadata.tax_invoice_date);
            const monthYear = `${certDate.toLocaleString('default', { month: 'short' })} ${certDate.getFullYear()}`;
            
            // Generate formatted filename for taxinvoiceslist
            const formattedFilename = generateFormattedFilename(
                metadata.serial_number,
                metadata.tax_invoice_ref,
                metadata.client_name,
                metadata.project_name,
                metadata.payment_certificate_no,
                monthYear,
                metadata.project_code
            );
            
            // Add formatted filename to metadata
            metadata.formatted_filename = formattedFilename;
            
            // Process and validate metadata (using existing logic)
            const processedMetadata = { ...metadata };
            
            // Validate numeric values
            Object.entries(processedMetadata).forEach(([key, value]) => {
                if (typeof value === 'number' && isNaN(value)) {
                    console.warn(`Invalid number found for ${key}, setting to 0`);
                    processedMetadata[key] = 0;
                }
            });

            formData.append('metadata', JSON.stringify(processedMetadata));
            formData.append('save_to_taxinvoiceslist', 'true');

            console.log('Sending request with taxinvoiceslist filename:', formattedFilename);

            const result = await axios.post(
                `${EXISTING_API_URL}/api/invoices/save-with-taxinvoiceslist`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                    withCredentials: true
                }
            );

            return result.data;
        } catch (error) {
            console.error('Error in saveInvoiceWithTaxInvoicesList:', error);
            if (error.response) {
                throw new Error(error.response.data?.message || error.response.data?.error || 'Server error occurred');
            } else if (error.request) {
                throw new Error('No response from server. Please check your connection.');
            } else {
                throw error;
            }
        }
    },

    // EXISTING FUNCTIONALITY - Get invoice download URL
    getInvoiceUrl: async (filePath) => {
        try {
            console.log('Getting invoice URL from EXISTING backend:', EXISTING_API_URL);
            const response = await axios.get(
                `${EXISTING_API_URL}/api/documents/view`,
                {
                    params: {
                        path: filePath
                    },
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    },
                    withCredentials: true
                }
            );
        
            if (!response.data?.url) {
                throw new Error('Invalid URL received from server');
            }
        
            return response.data.url;
        
        } catch (error) {
            console.error('Error getting invoice URL:', error);
            throw new Error('Failed to get invoice URL');
        }
    },

    // EXISTING FUNCTIONALITY - Upload invoice with metadata extraction
    uploadInvoice: async (file, clientName, onProgress) => {
        try {
            console.log('Uploading invoice to EXISTING backend:', EXISTING_API_URL);
            const formData = new FormData();
            formData.append('file', file);
            formData.append('client_name', clientName);

            const response = await axios.post(
                `${EXISTING_API_URL}/api/invoice/upload`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                    withCredentials: true,
                    onUploadProgress: (progressEvent) => {
                        if (onProgress) {
                            const progress = (progressEvent.loaded / progressEvent.total) * 100;
                            onProgress(Math.round(progress));
                        }
                    }
                }
            );

            return {
                success: response.data.status === 'success',
                data: response.data.data,
                message: response.data.message
            };

        } catch (error) {
            console.error('Invoice upload error:', error);
            throw new Error(error.response?.data?.message || 'Failed to upload invoice');
        }
    },

    // EXISTING FUNCTIONALITY - Get invoice details with financials
    getInvoiceDetails: async (invoiceId) => {
        try {
            console.log('Fetching invoice details from EXISTING backend:', EXISTING_API_URL);
            const response = await existingFlaskApi.get(`/api/invoice/${invoiceId}/details`);
            return response.data;
        } catch (error) {
            console.error('Error fetching invoice details:', error);
            throw new Error('Failed to fetch invoice details');
        }
    }
};

export default invoiceService;